import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import { brl, pct, num, Insufficient } from './format';
import { LimitControl } from './LimitControl';
import { OrdersHistogram } from './OrdersHistogram';

const DISCLAIMER = 'Custo estimado com base no frete cobrado em pedidos semelhantes (mesmo estado e forma de entrega), ajustado pelo fator de custo da loja. Não considera regras regionais de frete grátis nem a mudança de comportamento dos compradores.';
const FACTOR_HELP = 'Quanto o frete custa para a loja em relação ao valor cobrado do cliente. Ex.: 80% se o contrato com a transportadora for mais barato que o frete cobrado.';

// o = [valor dos produtos, frete cobrado, frete grátis hoje (R$ 0,00), custo estimado sem fator]; sem retirada.
const sumCost = (list, k) => list.reduce((s, o) => s + (o[3] || 0), 0) * k;

// Only orders between the current limit L and the simulated X change status; the rest stays as today.
const simulate = (orders, current, target, periodDays, factor) => {
  if (!orders?.length || !(current > 0) || !(target > 0)) return null;
  const k = ((factor ?? 100) / 100) * (30 / periodDays);
  const nowFree = orders.filter((o) => o[2]);
  const nowCost = sumCost(nowFree, k);
  const moved = target < current
    ? orders.filter((o) => o[0] >= target && o[0] < current && !o[2])
    : orders.filter((o) => o[0] >= current && o[0] < target && o[2]);
  const diff = (target < current ? 1 : -1) * sumCost(moved, k);
  const free = nowFree.length + (target < current ? moved.length : -moved.length);
  const near = orders.filter((o) => o[0] >= target * 0.8 && o[0] < target).length;
  return { pct: (free / orders.length) * 100, simCost: nowCost + diff, nowCost, diff, near };
};

const composition = (orders, current, periodDays, factor) => {
  if (!orders?.length || !(current > 0)) return null;
  const k = ((factor ?? 100) / 100) * (30 / periodDays);
  const n = orders.length;
  const rule = orders.filter((o) => o[2] && o[0] >= current);
  const outside = orders.filter((o) => o[2] && o[0] < current);
  const above = orders.filter((o) => o[0] >= current);
  const abovePaid = above.filter((o) => !o[2]);
  return {
    rule: { pct: (rule.length / n) * 100, cost: sumCost(rule, k) },
    outside: { pct: (outside.length / n) * 100, cost: sumCost(outside, k) },
    abovePaid: { pct: above.length ? (abovePaid.length / above.length) * 100 : null },
  };
};

const Origin = ({ label, pctValue, cost, help, testId }) => (
  <div className="rounded-lg border p-4 min-w-0" data-testid={testId}>
    <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    <p className="font-mono text-xl font-bold mt-1">{pct(pctValue)}</p>
    {cost != null && <p className="text-xs font-mono text-muted-foreground">{brl(cost)}/mês</p>}
    {help && <p className="text-[11px] text-muted-foreground mt-1">{help}</p>}
  </div>
);

const OriginBlock = ({ data }) => (
  <div data-testid="free-shipping-origin">
    <p className="font-medium mb-2">De onde vem o frete grátis hoje</p>
    <div className="grid gap-3 md:grid-cols-3">
      <Origin testId="origin-rule" label="Pela regra" pctValue={data.rule.pct} cost={data.rule.cost} help="Pedidos com frete grátis e valor igual ou acima do limite atual." />
      <Origin testId="origin-outside" label="Fora da regra" pctValue={data.outside.pct} cost={data.outside.cost} help="Cupons, promoções, regras por região ou produtos com frete grátis" />
      <Origin testId="origin-above-paid" label="Acima do limite, mas pagaram frete" pctValue={data.abovePaid.pct} help="Possivelmente regiões fora da regra ou opção de entrega expressa" />
    </div>
  </div>
);

const ZeroFreightFormas = ({ rows }) => (
  <div className="rounded-lg border p-4" data-testid="zero-freight-formas">
    <p className="text-sm font-medium mb-2">Formas de entrega com frete R$ 0,00 no período</p>
    <ul className="text-sm space-y-1">
      {rows.map((r) => (
        <li key={r.forma_entrega} className="flex flex-wrap items-center gap-2">
          <span>{r.forma_entrega}</span>
          <span className="text-xs text-muted-foreground">{num(r.pedidos)} pedidos</span>
          <span className={`text-[11px] rounded-full px-2 py-0.5 ${r.retirada ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
            {r.retirada ? 'Retirada (sem custo de frete)' : 'Entrega'}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const FactorEditor = ({ value, onSave }) => {
  const [v, setV] = useState(value ?? 100);
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(value ?? 100), [value]);
  const save = async () => {
    setSaving(true);
    try { await onSave(Number(v)); toast.success('Fator de custo salvo'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível salvar o fator'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-1" data-testid="cost-factor-editor">
      <label className="text-xs text-muted-foreground">Fator de custo da loja (%)</label>
      <div className="flex items-center gap-2">
        <Input type="number" min="1" max="500" step="1" value={v} onChange={(e) => setV(e.target.value)} className="w-28" data-testid="cost-factor-input" />
        <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="cost-factor-save">Salvar</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">{FACTOR_HELP}</p>
    </div>
  );
};

const Diff = ({ value }) => {
  const zero = Math.abs(value) < 0.005;
  const up = value > 0;
  return (
    <p className={`font-mono text-xl font-bold ${zero ? 'text-foreground' : up ? 'text-red-600' : 'text-green-600'}`} data-testid="simulator-diff">
      {zero ? '' : up ? '+' : '−'}{brl(Math.abs(value))}/mês <span className="text-xs font-normal text-muted-foreground">em relação a hoje</span>
    </p>
  );
};

export const FreeShippingSection = ({ report, editable, onSaveLimit, onSaveFactor, notice, auditApi }) => {
  const factor = report.frete_fator_custo ?? 100;
  const { histogram, simulator_orders: orders, detected_limit: detected, frete_gratis_limite: limit, days } = report;
  const maxVal = useMemo(() => Math.max(...(orders || []).map((o) => o[0]), 100), [orders]);
  const initial = limit ?? detected ?? Math.round(maxVal / 2);
  const [sim, setSim] = useState(initial);
  useEffect(() => setSim(initial), [initial]);
  const configured = report.frete_gratis_configurado ?? limit != null;
  const current = limit ?? (configured ? Infinity : null);
  const res = simulate(orders, current, Number(sim), days, factor);
  const limitCtl = <LimitControl limit={limit} configured={configured} detected={detected} onSave={onSaveLimit} auditApi={auditApi} />;
  const comp = composition(orders, limit, days, factor);
  if (!histogram?.length) {
    return (
      <div className="space-y-4">
        {limitCtl}
        <Insufficient message={notice} testId="free-shipping-insufficient" />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {limitCtl}
      {editable && report.formas_frete_zero?.length > 0 && <ZeroFreightFormas rows={report.formas_frete_zero} />}
      {comp && <OriginBlock data={comp} />}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="min-w-0 lg:col-span-3">
          <CardContent className="p-4">
            <OrdersHistogram orders={orders || []} limit={limit} sim={sim} />
          </CardContent>
        </Card>
        <Card className="min-w-0 lg:col-span-2" data-testid="free-shipping-simulator">
          <CardContent className="p-4 space-y-4">
            <p className="font-medium">Simulador de frete grátis</p>
            <div className="flex items-center gap-3">
              <Slider value={[Number(sim) || 0]} min={0} max={Math.ceil(maxVal)} step={1} onValueChange={([v]) => setSim(v)} className="flex-1" data-testid="simulator-slider" />
              <Input type="number" min="0" value={sim} onChange={(e) => setSim(e.target.value)} className="w-28" data-testid="simulator-input" />
            </div>
            {res ? (
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Pedidos que se qualificariam</p><p className="font-mono text-xl font-bold" data-testid="simulator-pct">{pct(res.pct)}</p></div>
                <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Custo estimado de frete grátis para a loja</p><p className="font-mono text-xl font-bold" data-testid="simulator-cost">{brl(res.simCost)}/mês</p></div>
                <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Custo atual de frete grátis</p><p className="font-mono text-base font-semibold" data-testid="simulator-current-cost">{brl(res.nowCost)}/mês</p></div>
                <div className="rounded-md bg-muted/50 p-3"><p className="text-xs text-muted-foreground uppercase tracking-wider">Diferença</p><Diff value={res.diff} /></div>
                <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Pedidos até 20% abaixo do limite</p><p className="font-mono text-xl font-bold" data-testid="simulator-near">{num(res.near)}</p></div>
              </div>
            ) : <p className="text-sm text-muted-foreground" data-testid="simulator-need-limit">{!configured ? 'Configure o limite atual de frete grátis para usar o simulador' : 'Informe um valor maior que zero.'}</p>}
            {editable ? <FactorEditor value={factor} onSave={onSaveFactor} /> : (
              <p className="text-[11px] text-muted-foreground" data-testid="cost-factor-readonly">Fator de custo da loja: {num(factor)}%</p>
            )}
            <p className="text-[11px] text-muted-foreground border-t pt-3" data-testid="simulator-disclaimer">{DISCLAIMER}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
